// Maps keywords from AI questions to their predefined answer options.
// The AI paraphrases questions, so we match by keywords — but ONLY
// against the actual question sentence(s), not the acknowledgment text.

export interface QuestionMapping {
  keywords: string[];
  /** Keywords that, if found in the question, disqualify this mapping */
  negativeKeywords?: string[];
  options: string[];
  multiSelect?: boolean;
}

/**
 * Patterns that indicate the AI is asking a free-text / open-ended question
 * where we should NOT show predefined buttons.
 */
const FREE_TEXT_PATTERNS: string[] = [
  'describe',
  'tell me about',
  'tell us about',
  'explain',
  'elaborate',
  'idea',
  'brief description',
  'more detail',
  'walk me through',
  'share your vision',
  'what does your project do',
  'what problem',
  'full name',
  'your name',
  'email',
  'phone',
  'contact',
  'specify',
];

const qqQuestionMappings: QuestionMapping[] = [
  {
    keywords: ['your role', 'role in the project', 'what is your role', 'role are you'],
    negativeKeywords: ['company size', 'budget', 'deadline'],
    options: [
      'Founder / Co-founder',
      'CTO / Technical Lead',
      'Product Manager',
      'Business Owner',
      'Student / Research / Just exploring',
      'Agency / Consultant (representing a client)',
      'Other',
    ],
  },
  {
    keywords: ['company size', 'how big is your company', 'how many employees', 'team size', 'size of your company', 'number of employees'],
    options: ['1–10', '11–50', '51–200', '200+'],
  },
  {
    keywords: ['company type', 'type of company', 'kind of company', 'type of organization', 'classify your company', 'category of your company'],
    negativeKeywords: ['company size'],
    options: [
      'Startup (pre-MVP / post-MVP)',
      'Small and Medium-sized Business',
      'Enterprise',
      'Agency / Consultancy',
    ],
  },
  {
    keywords: ['type of software', 'software project are you planning', 'what kind of application', 'type of project', 'what are you building', 'type of application'],
    negativeKeywords: ['company type', 'company size', 'budget'],
    options: [
      'Not sure',
      'Mobile Application',
      'Web Application',
      'Desktop Application',
      'Enterprise Software',
      'Embedded/IoT System',
      'E-commerce Platform',
    ],
  },
  {
    keywords: ['which industry', 'what industry', 'industry does your', 'industry sector', 'which sector'],
    negativeKeywords: ['budget', 'deadline', 'features'],
    options: [
      'Retail',
      'Healthcare',
      'Finance',
      'E-commerce',
      'Logistics',
      'SaaS',
      'Other',
    ],
  },
  {
    keywords: ['project scope', 'describes your project scope', 'stage of the project', 'scope of your project', 'what best describes your project'],
    negativeKeywords: ['budget', 'deadline', 'features'],
    options: [
      'MVP (Minimum Viable Product)',
      'Full product',
      'Redesign / Rebuild',
      'Feature expansion',
      'Proof of concept',
    ],
  },
  {
    keywords: ['key features', 'features your software', 'features will include', 'functionalities do you need', 'features do you need'],
    negativeKeywords: ['critical', 'initial version', 'budget'],
    options: [
      'Not sure',
      'User Authentication',
      'Real-time Data Processing',
      'Database Integration',
      'E-commerce Functions',
      'Real-time Communication',
      'Payment Gateway Integration',
      'Multi-platform Accessibility',
      'API Integrations',
      'Custom Content Management',
    ],
    multiSelect: true,
  },
  {
    keywords: ['critical for your initial', 'critical features', 'must-have features', 'initial version', 'first version', 'critical for the first'],
    negativeKeywords: ['key features your software will include', 'budget'],
    options: [
      'Not sure',
      'User Login System',
      'Shopping Cart and Checkout',
      'Real-time Notifications',
      'Basic Reporting and Analytics',
      'Content Management Tools',
    ],
    multiSelect: true,
  },
  {
    keywords: ['primary user', 'who will use', 'target user', 'end user', 'who is the user', 'who are the users', 'intended audience'],
    negativeKeywords: ['budget', 'deadline'],
    options: [
      'Not sure',
      'General Consumers',
      'Business Users',
      'Tech Enthusiasts',
      'Specific Industry Segment',
      'Other',
    ],
  },
  {
    keywords: ['when do you need', 'by when', 'project completed', 'target deadline', 'delivery date', 'when should it be ready', 'when would you like'],
    negativeKeywords: ['budget'],
    options: [
      'No specific deadline',
      'Within 1 month',
      '1-3 months',
      '3-6 months',
      'More than 6 months',
    ],
  },
  {
    keywords: ['budget range', 'budget for this project', 'how much are you willing', 'investment range', 'financial range', 'budget are you considering'],
    options: [
      'Not sure',
      'Under €10k',
      '€10k–€25k',
      '€25k–€50k',
      '€50k+',
    ],
  },
];

const n2nQuestionMappings: QuestionMapping[] = [
  {
    keywords: ['what do you need help with', 'what type of service', 'what kind of service', 'how can we help', 'what are you looking for'],
    negativeKeywords: ['budget', 'deadline'],
    options: [
      'Build a new product',
      'Improve an existing one',
      'Modernize a legacy system',
      'Dedicated development team',
      'Not sure yet',
    ],
  },
  {
    keywords: ['budget range', 'budget for this project', 'how much are you willing', 'investment range', 'financial range', 'budget are you considering'],
    options: [
      'Not sure',
      'Under €10k',
      '€10k–€25k',
      '€25k–€50k',
      '€50k+',
    ],
  },
];

/**
 * Extracts the question part from an AI message.
 * The AI typically starts with an acknowledgment/context sentence,
 * then asks the actual question. We extract only the question portion.
 */
function extractQuestionPart(text: string): string {
  // Split into sentences (handle ?, !, and . as terminators)
  const sentences = text.split(/(?<=[.!?])\s+/);

  // Collect sentences that contain a question mark — those are the actual questions
  const questionSentences = sentences.filter((s) => s.includes('?'));

  if (questionSentences.length > 0) {
    return questionSentences.join(' ').toLowerCase();
  }

  // Fallback: if no question mark found, use the last sentence
  // (the AI usually puts the question last)
  if (sentences.length > 1) {
    return sentences[sentences.length - 1].toLowerCase();
  }

  return text.toLowerCase();
}

/**
 * Checks if the question part looks like a free-text / open-ended question
 * where predefined buttons would not make sense.
 */
function isFreeTextQuestion(questionPart: string): boolean {
  return FREE_TEXT_PATTERNS.some((pattern) => questionPart.includes(pattern));
}

/**
 * Detects if an AI message matches a known question and returns the options.
 * Only matches for the 'qq' (QuickQuote) service.
 *
 * Key improvements:
 * - Only matches against the question sentence(s), not the full message
 * - Supports negative keywords to prevent false positives
 * - Excludes free-text questions (describe your idea, name, email, etc.)
 */
export function detectQuickReplyOptions(
  messageText: string,
  serviceId?: string,
): QuestionMapping | null {
  let mappings: QuestionMapping[];
  if (serviceId === 'qq') {
    mappings = qqQuestionMappings;
  } else if (serviceId === 'n2n') {
    mappings = n2nQuestionMappings;
  } else {
    return null;
  }

  const questionPart = extractQuestionPart(messageText);

  // Don't show buttons for free-text questions
  if (isFreeTextQuestion(questionPart)) return null;

  for (const mapping of mappings) {
    const hasPositive = mapping.keywords.some((kw) => questionPart.includes(kw));
    if (!hasPositive) continue;

    // Check negative keywords against the question part
    const hasNegative = mapping.negativeKeywords?.some((nk) => questionPart.includes(nk));
    if (hasNegative) continue;

    return mapping;
  }

  return null;
}
